import { graphql, useStaticQuery } from "gatsby";
import { FooterContent } from "@/types/footer";
import { BannerItem } from "@/types/banner";
import {
  transformFooterData,
  transformNavbarData,
  transformBannerData,
  NavbarData,
} from "./layoutTransformers";

interface ComponentConfig {
  name: string;
}

export interface LayoutData {
  footerContent: FooterContent;
  navbarData: NavbarData;
  bannerItems: BannerItem[];
  components: ComponentConfig[];
  useUtilityBanner: boolean;
  billingUrl: string | null;
  patientPortalUrl: string | null;
}

export const useLayoutData = (): LayoutData => {
  const data = useStaticQuery(graphql`
    query LayoutDataQuery {
      allStrapiService(sort: [{ sortOrder: ASC }, { name: ASC }]) {
        nodes {
          slug
          name
        }
      }
      allStrapiHomePageContent {
        nodes {
          logoUrl
          heroPrimaryButtonText
          heroPrimaryButtonURL
          navbarSecondaryCTA
          navbarSecondaryCTAUrl
          billingUrl
          patientPortalUrl
          useUtilityBanner
        }
      }
      allStrapiResource {
        nodes {
          slug
        }
      }
      strapiFooter {
        id
        copyright
        logo {
          src
          alt
        }
        contact {
          phone
          fax
          address
          officeHours
          bookingLink
          billingPhone
          billingEmail
        }
        legalLinks {
          label
          url
          columnName
          opensInNewTab
        }
        columns {
          label
          url
          columnName
        }
        socialLinks {
          label
          url
          columnName
        }
      }
      allStrapiHomePage {
        nodes {
          components {
            strapi_json_value {
              name
            }
          }
        }
      }
      allStrapiBanner(sort: { sortOrder: ASC }) {
        nodes {
          id
          text
          linkText
          linkUrl
          sortOrder
        }
      }
    }
  `);

  const components = data.allStrapiHomePage.nodes[0].components.strapi_json_value;
  const homePageContent = data.allStrapiHomePageContent.nodes[0];
  const serviceNodes = data.allStrapiService.nodes;
  const resourceCount = data.allStrapiResource.nodes.length;
  const bannerNodes = data.allStrapiBanner?.nodes || [];

  return {
    footerContent: transformFooterData(data.strapiFooter),
    navbarData: transformNavbarData(homePageContent, serviceNodes, resourceCount),
    bannerItems: transformBannerData(bannerNodes),
    components,
    useUtilityBanner: homePageContent.useUtilityBanner ?? false,
    billingUrl: homePageContent.billingUrl ?? null,
    patientPortalUrl: homePageContent.patientPortalUrl ?? null,
  };
};
